// src/store/slices/heliaSlice.ts
import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import type { HeliaStatus, HeliaNode, FileMetadata } from '@/types/poc/helia';
import {
  addFileToHelia,
  getFileFromHelia,
  downloadAndDecryptFile,
  downloadFileFromNetwork,
  saveFileToFilesystem,
  testCryptoEndToEnd,
  testUkekMismatch,
  testPrivateKeyEncryption
} from '@/store/thunks/poc/helia';

const PROGRESS_COMPLETE = 100;

export interface UploadProgress {
  fileName: string;
  cid?: string;
  stage: 'encrypting' | 'uploading' | 'announcing' | 'hap-downloading' | 'hap-registering' | 'complete' | 'error';
  progress: number;
  error?: string;
  hapBytesTransferred?: number;
  hapTotalBytes?: number;
  sixId?: string;
  metadata?: FileMetadata;
  retryCount?: number;
}

// Define the Helia state interface
export interface HeliaState {
  status: HeliaStatus;
  node: HeliaNode | null;
  files: Record<string, FileMetadata>; // CID string -> FileMetadata
  errorMessage: string | null;
  currentOperation: string | null;

  // Upload progress tracking (by file name)
  uploadProgress: Record<string, UploadProgress>;

  // Network file operations state
  networkOperations: Record<string, {
      status: 'requesting' | 'downloading' | 'completed' | 'failed';
      progress: number;
      error?: string;
    }>;
}

// Initial state
const initialState: HeliaState = {
  status: 'idle',
  node: null,
  files: {},
  errorMessage: null,
  currentOperation: null,
  uploadProgress: {},
  networkOperations: {},
};

const heliaSlice = createSlice({
  name: 'helia',
  initialState,
  reducers: {
    // Pure function for manually setting the node (useful for testing)
    // Add file metadata manually
    addFileMetadata: (state, action: PayloadAction<FileMetadata>) => {
      const { payload } = action;
      const { cid } = payload;
      state.files[cid] = payload;
    },

    // Remove file metadata
    removeFileMetadata: (state, action: PayloadAction<string>) => {
      const { payload } = action;
      const { [payload]: removedFile, ...remainingFiles } = state.files;
      state.files = remainingFiles;
      // Also clean up network operation state
      const { [payload]: removedOp, ...remainingOps } = state.networkOperations;
      state.networkOperations = remainingOps;
    },

    // Network operation progress tracking
    setNetworkOperationStatus: (
      state,
      action: PayloadAction<{
        cid: string;
        status: HeliaState['networkOperations'][string]['status'];
        progress?: number;
        error?: string;
      }>
    ) => {
      const { payload } = action;
      const { cid, status, progress = 0, error } = payload;
      state.networkOperations[cid] = {
        status,
        progress,
        ...((error != null) && { error })
      };
    },

    // Clear network operation tracking
    clearNetworkOperation: (state, action: PayloadAction<string>) => {
      const { payload } = action;
      const { [payload]: removedOp, ...remainingOps } = state.networkOperations;
      state.networkOperations = remainingOps;
    },

    // Upload progress tracking
    setUploadProgress: (
      state,
      action: PayloadAction<{
        fileName: string;
        cid?: string;
        stage: UploadProgress['stage'];
        progress: number;
        error?: string;
        hapBytesTransferred?: number;
        hapTotalBytes?: number;
        sixId?: string;
        metadata?: FileMetadata;
        retryCount?: number;
      }>
    ) => {
      const { payload } = action;
      const existing = state.uploadProgress[payload.fileName];
      state.uploadProgress[payload.fileName] = {
        ...existing,
        ...payload,
        error: payload.stage === 'error' ? payload.error : undefined,
      };
    },

    // Clear upload progress for a file
    clearUploadProgress: (state, action: PayloadAction<string>) => {
      const { payload } = action;
      const { [payload]: removedProgress, ...remainingProgress } = state.uploadProgress;
      state.uploadProgress = remainingProgress;
    },

    // Clear error message
    clearError: (state) => {
      state.errorMessage = null;
    },
  },

  extraReducers: (builder) => {
    builder
      // Add file cases
      .addCase(addFileToHelia.pending, (state) => {
        state.currentOperation = 'Adding file to IPFS';
      })
      .addCase(addFileToHelia.fulfilled, (state, action) => {
        const { payload } = action;
        state.files[payload.cid] = payload;
        state.currentOperation = null;
      })
      .addCase(addFileToHelia.rejected, (state, action) => {
        state.errorMessage = typeof action.payload === 'string' ? action.payload : 'Failed to add file';
        state.currentOperation = null;
      })

      // Get file cases
      .addCase(getFileFromHelia.pending, (state) => {
        state.currentOperation = 'Retrieving file from IPFS';
      })
      .addCase(getFileFromHelia.fulfilled, (state) => {
        state.currentOperation = null;
      })
      .addCase(getFileFromHelia.rejected, (state, action) => {
        state.errorMessage = typeof action.payload === 'string' ? action.payload : 'Failed to add file';
        state.currentOperation = null;
      })

      // ===== NEW NETWORK FILE CASES =====

      // Download from network cases
      .addCase(downloadFileFromNetwork.pending, (state, action) => {
        const { meta: { arg: { cid } } } = action;
        state.currentOperation = `Downloading ${cid} from network`;
        state.networkOperations[cid] = {
          status: 'requesting',
          progress: 0
        };
      })
      .addCase(downloadFileFromNetwork.fulfilled, (state, action) => {
        const { meta: { arg: { cid } } } = action;
        state.currentOperation = null;
        state.networkOperations[cid] = {
          status: 'completed',
          progress: PROGRESS_COMPLETE
        };
      })
      .addCase(downloadFileFromNetwork.rejected, (state, action) => {
        const { meta: { arg: { cid } }, error: { message } } = action;
        state.currentOperation = null;
        state.errorMessage = message ?? 'Failed to download file';
        state.networkOperations[cid] = {
          status: 'failed',
          progress: 0,
          error: action.error.message
        };
      })

      // Save to filesystem cases
      .addCase(saveFileToFilesystem.pending, (state) => {
        state.currentOperation = 'Saving file to filesystem';
      })
      .addCase(saveFileToFilesystem.fulfilled, (state) => {
        state.currentOperation = null;
      })
      .addCase(saveFileToFilesystem.rejected, (state, action) => {
        const { error: { message } } = action;
        state.currentOperation = null;
        state.errorMessage = message ?? 'Failed to save file';
      })

      // Download and decrypt cases
      .addCase(downloadAndDecryptFile.pending, (state) => {
        state.currentOperation = 'Downloading and decrypting file';
      })
      .addCase(downloadAndDecryptFile.fulfilled, (state) => {
        state.currentOperation = null;
      })
      .addCase(downloadAndDecryptFile.rejected, (state, action) => {
        const { error: { message } } = action;
        state.currentOperation = null;
        state.errorMessage = message ?? 'Failed to download and decrypt file';
      })

      // Test crypto end-to-end cases
      .addCase(testCryptoEndToEnd.pending, (state) => {
        state.currentOperation = 'Running crypto end-to-end test';
      })
      .addCase(testCryptoEndToEnd.fulfilled, (state) => {
        state.currentOperation = null;
      })
      .addCase(testCryptoEndToEnd.rejected, (state, action) => {
        const { error: { message } } = action;
        state.currentOperation = null;
        state.errorMessage = message ?? 'Crypto test failed';
      })

      // Test private key encryption cases
      .addCase(testPrivateKeyEncryption.pending, (state) => {
        state.currentOperation = 'Testing private key encryption';
      })
      .addCase(testPrivateKeyEncryption.fulfilled, (state) => {
        state.currentOperation = null;
      })
      .addCase(testPrivateKeyEncryption.rejected, (state, action) => {
        const { error: { message } } = action;
        state.currentOperation = null;
        state.errorMessage = message ?? 'Private key test failed';
      })

      // Test UKEK mismatch cases
      .addCase(testUkekMismatch.pending, (state) => {
        state.currentOperation = 'Testing UKEK mismatch';
      })
      .addCase(testUkekMismatch.fulfilled, (state) => {
        state.currentOperation = null;
      })
      .addCase(testUkekMismatch.rejected, (state, action) => {
        const { error: { message } } = action;
        state.currentOperation = null;
        state.errorMessage = message ?? 'UKEK test failed';
      });
  },
});

const { actions } = heliaSlice;
export const {
  addFileMetadata,
  removeFileMetadata,
  setNetworkOperationStatus,
  clearNetworkOperation,
  setUploadProgress,
  clearUploadProgress,
  clearError,
} = actions;

export default heliaSlice.reducer;
